import { action, makeObservable, observable, runInAction } from "mobx";
import { InvoiceRequest } from "../../../models/Invoice/InvoiceRequest";
import {
  Invoice,
  InvoicePayment,
  InvoicePaymentRequest,
} from "../../../models/Invoice/Invoice";
import { InvoiceFilters } from "../../../models/Invoice/InvoiceFilters";
import InvoiceAPI from "../api/InvoiceAPI";
import ImageAPI from "../api/ImageAPI";
import {
  APIBlobResponse,
  PaginatedAPIResponse,
} from "../../../models/NetworkResponse";
import { Address } from "../../../models/Address/Address";
import { OrderProduct, OrderServices } from "../../../models/Order/OrderProduct";
import { AdvancePayment } from "../../../models/Order/AdvancePayment";
import dayjs, { Dayjs } from "dayjs";
import { GetPurchaseOrderInvoicesParams } from "../../../models/Invoice/PurchaseOrderInvoiceFilters";
import {
  PurchaseOrderInvoice,
  ProductSelectionState,
} from "../../../models/Invoice/PurchaseOrderInvoice";
import { PurchaseOrderInvoiceUploadRequest } from "../../../models/Invoice/PurchaseOrderInvoiceUploadRequest";
import { ActivityLog } from "../../../models/Activity/ActivityLog";

export class InvoiceStore {
  invoiceAPI: InvoiceAPI;
  orderInvoiceDetails: Invoice;
  invoiceRequest: InvoiceRequest;
  invoiceDetails: Invoice;
  advancePayments: AdvancePayment[] = [];
  invoices: Invoice[] = [];
  purchaseOrderInvoices: PurchaseOrderInvoice[] = [];
  limit: number = 0;
  total_count: number = 0;
  total_pages: number = 0;
  approvePurchaseOrderInvoicePopup: { open: boolean; invoiceId: number | null };
  rejectPurchaseOrderInvoicePopup: { open: boolean; invoiceId: number | null };
  invoiceUploadState: {
    dialogOpen: boolean;
    selectedPoId: number | null;
    vendorId: number | null;
    invoiceFile: File | null;
    invoiceNumber: string;
    invoiceAmount: string;
    invoiceDate: Dayjs | null;
    isUploading: boolean;
    otherChargesEnabled: boolean;
    otherChargesAmount: string;
    productSelections: Map<string, ProductSelectionState>;
    invoiceFileUrl: string;
    productsAccordionExpanded: boolean;
  };
  invoiceFilter: string;
  activityLogs: ActivityLog[] = [];

  constructor() {
    this.orderInvoiceDetails = {} as Invoice;
    this.invoiceRequest = {} as InvoiceRequest;
    this.invoiceAPI = new InvoiceAPI();
    this.invoiceDetails = {} as Invoice;
    this.advancePayments = [];
    this.invoices = [];
    this.purchaseOrderInvoices = [];
    this.limit = 0;
    this.total_count = 0;
    this.total_pages = 0;
    this.approvePurchaseOrderInvoicePopup = { open: false, invoiceId: null };
    this.rejectPurchaseOrderInvoicePopup = { open: false, invoiceId: null };
    this.invoiceUploadState = {
      dialogOpen: false,
      selectedPoId: null,
      invoiceFile: null,
      invoiceNumber: "",
      invoiceAmount: "",
      invoiceDate: null,
      vendorId: null,
      isUploading: false,
      otherChargesEnabled: false,
      otherChargesAmount: "",
      productSelections: new Map(),
      invoiceFileUrl: "",
      productsAccordionExpanded: true,
    };
    this.invoiceFilter = "PENDING";
    this.activityLogs = [];

    makeObservable(this, {
      orderInvoiceDetails: observable,
      invoiceRequest: observable,
      invoiceDetails: observable,
      updateBillingAddress: action,
      updateAdvancePayment: action,
      advancePayments: observable,
      getAdvancePayments: action,
      updateBillingFromAddress: action,
      downloadInvoicesInBackground: action,
      invoices: observable,
      limit: observable,
      total_count: observable,
      total_pages: observable,
      getInvoices: action,
      purchaseOrderInvoices: observable,
      getPurchaseOrderInvoices: action,
      approvePurchaseOrderInvoiceRequest: action,
      rejectPurchaseOrderInvoiceRequest: action,
      markPurchaseOrderInvoicePaymentStatus: action,
      openApprovePurchaseOrderInvoicePopup: action,
      openRejectPurchaseOrderInvoicePopup: action,
      approvePurchaseOrderInvoicePopup: observable,
      rejectPurchaseOrderInvoicePopup: observable,
      invoiceUploadState: observable,
      uploadPurchaseOrderInvoice: action,
      openInvoiceUploadDialog: action,
      closeInvoiceUploadDialog: action,
      setInvoiceUploadFile: action,
      setInvoiceUploadNumber: action,
      setInvoiceUploadAmount: action,
      setInvoiceUploadDate: action,
      validateInvoiceUpload: action,
      setOtherChargesEnabled: action,
      setOtherChargesAmount: action,
      setProductSelections: action,
      setInvoiceFileUrl: action,
      setProductsAccordionExpanded: action,
      resetInvoiceUploadState: action,
      activityLogs: observable,
      getInvoiceActivityLogs: action,
      clearActivityLogs: action,
    });
  }

  updateBillingAddress = (address: Address | undefined) => {
    this.invoiceRequest = {
      ...this.invoiceRequest,
      billing_address: address,
      billing_address_id: address?.id,
      place_of_supply: address?.state_code,
    };
  };

  updateShippingAddress = (address: Address | undefined) => {
    this.invoiceRequest = {
      ...this.invoiceRequest,
      shipping_address: address,
      shipping_address_id: address?.id,
    };
  };

  updateBillingFromAddress = (address: Address | undefined) => {
    this.invoiceRequest = {
      ...this.invoiceRequest,
      bill_from_address: address,
      bill_from_address_id: address?.id,
    };
  };

  updateInvoiceRequest() {
    this.invoiceRequest = {
      ...this.invoiceRequest,
      items: this.invoiceDetails.items?.map((product) => {
        return {
          ...product,
          name: product.product_name,
          id: product.product_id,
        };
      }),
      services: this.invoiceDetails.services?.map((service) => {
        return {
          ...service,
          id: service.service_id,
          name: service.service_name,
        };
      }),
      payments: this.invoiceDetails.payments?.map((payment) => payment),
      billing_address: this.invoiceDetails.billing_address,
      shipping_address: this.invoiceDetails.shipping_address,
      bill_from_address: this.invoiceDetails.bill_from_address,
      place_of_supply: this.invoiceDetails.place_of_supply,
      date: dayjs(this.invoiceDetails.date)?.format("YYYY-MM-DD HH:mm:ss"),
      active: this.invoiceDetails.active,
    };
  }

  createOrUpdateInvoice = async (orderId: number, invoiceId?: number) => {
    try {
      const date = new Date();
      const todaysDate = date.toISOString().split("T")[0];
      const invoiceRequestParams: InvoiceRequest = {
        order_id: orderId,
        bill_from_address_id: this.invoiceRequest.bill_from_address?.id,
        billing_address_id: this.invoiceRequest.billing_address?.id,
        shipping_address_id: this.invoiceRequest.shipping_address?.id,
        gst_no: this.invoiceRequest.billing_address?.gstin,
        date: this.invoiceRequest.date ? this.invoiceRequest.date : todaysDate,
        items: this.invoiceRequest.items,
        services: this.invoiceRequest.services,
        place_of_supply: this.invoiceRequest.place_of_supply,
        id:
          this.invoiceDetails && this.invoiceDetails.id
            ? this.invoiceDetails.id
            : undefined,
      };
      if (invoiceId) {
        const response =
          await this.invoiceAPI.updateInvoice(invoiceRequestParams);
        this.invoiceDetails = response;
        this.updateInvoiceRequest();
      } else {
        const response =
          await this.invoiceAPI.createInvoice(invoiceRequestParams);
        this.invoiceDetails = response;
        this.updateInvoiceRequest();
      }
    } catch (error: any) {
      throw error;
    }
  };

  getOrderDetailsById = async (orderId: number) => {
    try {
      const details = await this.invoiceAPI.getOrderDetailsById(orderId);
      if (details) {
        this.invoiceRequest = {
          ...this.invoiceRequest,
          order_id: details.id,
        };

        this.orderInvoiceDetails = {
          ...this.orderInvoiceDetails,
          customer_id: Number(details.customer?.id),
          customer_name: details.customer?.name,
          billing_address_id: Number(details.billing_address_id),
          items: details.products,
          services: details.services,
          shipping_address_id: Number(details.shipping_address_id),
          amount_before_discount: Number(details.amount_before_discount),
          net_amount: details.net_amount,
          discount: details.discount,
          tax_amount: details.tax_amount,
          total_amount: details.total_amount,
          order_id: details.id,
        };
      }
    } catch (error: any) {
      throw error;
    }
  };

  getInvoiceDetailsById = async (invoiceNumber: number) => {
    try {
      const invoiceDetails =
        await this.invoiceAPI.getInvoiceDetailsById(invoiceNumber);

      this.invoiceDetails = invoiceDetails;
      this.updateInvoiceRequest();
    } catch (error: any) {
      throw error;
    }
  };

  addPaymentInfo = async (paymentInfo: InvoicePaymentRequest) => {
    const response = await this.invoiceAPI.addPaymentInfo(paymentInfo);
    this.invoiceRequest.payments = response.payments;
    return response;
  };

  updateConfirmedProducts(selectedProducts: OrderProduct[]) {
    const items = this.invoiceRequest.items || [];
    selectedProducts.forEach((selectedProduct) => {
      const productIndex = items.findIndex(
        (product) => product.id === selectedProduct.id,
      );

      if (productIndex !== -1) {
        items[productIndex] = selectedProduct;
      } else {
        items.push(selectedProduct);
      }
    });
    items.forEach((item) => {
      item.product_id = item.id;
    });
    this.invoiceRequest = {
      ...this.invoiceRequest,
      items: items,
    };
  }

  updateConfirmedServices(selectedServices: OrderServices[]) {
    const services = this.invoiceRequest.services || [];
    selectedServices.forEach((selectedService) => {
      const serviceIndex = services.findIndex(
        (service) =>
          service.id === selectedService.id &&
          service.custom_name === selectedService.custom_name,
      );

      if (serviceIndex !== -1) {
        services[serviceIndex] = selectedService;
      } else {
        services.push(selectedService);
      }
    });
    services.forEach((service) => {
      service.service_id = service.id;
    });
    this.invoiceRequest = {
      ...this.invoiceRequest,
      services: services,
    };
  }

  updateProductQuantity(productId: number, quantity: number) {
    const index = this.invoiceRequest.items!.findIndex(
      (product) => product.id === productId,
    );
    if (index !== -1) {
      this.invoiceRequest.items![index].quantity = quantity;
    }
  }

  updateServiceQuantity(
    serviceId: number,
    custom_name: string,
    quantity: number,
  ) {
    const index = this.invoiceRequest.services!.findIndex(
      (service) =>
        service.id === serviceId && service.custom_name === custom_name,
    );
    if (index !== -1) {
      this.invoiceRequest.services![index].quantity = quantity;
    }
  }

  deletePayment = async (deletePayment: InvoicePaymentRequest) => {
    try {
      const response = await this.invoiceAPI.deletePayment(deletePayment);
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  removePaymentLocally = (removePayment: InvoicePaymentRequest) => {
    const index = this.invoiceRequest.payments!.findIndex(
      (service) => service.id === removePayment.id,
    );
    if (index !== -1) {
      this.invoiceRequest.payments?.splice(index, 1);
    }
  };

  addPaymentLocally = (addPayment: InvoicePaymentRequest) => {
    this.invoiceRequest.payments?.push(addPayment as InvoicePayment);
  };

  deleteServiceLocally = (serviceId: number, custom_name: string) => {
    const confirmedServices = this.invoiceRequest.services;
    if (confirmedServices) {
      const updatedServices = confirmedServices.filter(
        (service) =>
          !(service.id === serviceId && service.custom_name === custom_name),
      );
      this.invoiceRequest = {
        ...this.invoiceRequest,
        services: updatedServices,
      };
    }
  };

  deleteProductLocally = (productId: number) => {
    const confirmedProducts = this.invoiceRequest.items;
    if (confirmedProducts) {
      const updatedProducts = confirmedProducts.filter(
        (product) => product.id !== productId,
      );
      this.invoiceRequest = {
        ...this.invoiceRequest,
        items: updatedProducts,
      };
    }
  };

  downloadInvoice = async (invoiceId: number): Promise<APIBlobResponse> => {
    try {
      const response = (await this.invoiceAPI.downloadInvoice(
        invoiceId,
      )) as APIBlobResponse;
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  downloadOldInvoice = async (invoiceId: number): Promise<APIBlobResponse> => {
    try {
      const response = (await this.invoiceAPI.downloadOldInvoice(
        invoiceId,
      )) as APIBlobResponse;
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  downloadInvoiceByOrderId = async (
    orderId: number,
  ): Promise<APIBlobResponse> => {
    try {
      const response = (await this.invoiceAPI.downloadInvoiceByOrderId(
        orderId,
      )) as APIBlobResponse;
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  downloadOldInvoiceByOrderId = async (
    orderId: number,
  ): Promise<APIBlobResponse> => {
    try {
      const response = (await this.invoiceAPI.downloadOldInvoiceByOrderId(
        orderId,
      )) as APIBlobResponse;
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  deleteInvoice = async (
    orderId: number,
    invoiceId: number,
    productId?: number,
    serviceId?: number,
    reason?: string,
  ) => {
    try {
      if (productId) {
        const res = await this.invoiceAPI.deleteInvoice(
          orderId,
          invoiceId,
          productId,
        );

        if (res) {
          this.invoiceRequest = {
            ...this.invoiceRequest,
            ...res,
          };
          this.invoiceDetails = res;
          this.updateInvoiceRequest();
        }
      } else if (serviceId) {
        const serviceName = this.invoiceRequest.services?.filter(
          (item) => item.service_id === serviceId,
        )[0]?.custom_name;
        const res = await this.invoiceAPI.deleteInvoice(
          orderId,
          invoiceId,
          undefined,
          serviceId,
          serviceName,
        );
        if (res) {
          this.invoiceDetails = res;
          this.invoiceRequest = {
            ...this.invoiceRequest,
            ...res,
          };
          this.updateInvoiceRequest();
        }
      } else {
        const res = await this.invoiceAPI.deleteInvoice(
          orderId,
          invoiceId,
          undefined,
          undefined,
          undefined,
          reason,
        );
        return res;
      }
    } catch (error: any) {
      throw error;
    }
  };

  reactivateInvoice = async (
    orderId: number,
    invoiceId: number,
    reason?: string,
  ): Promise<Invoice> => {
    try {
      const res = await this.invoiceAPI.reactivateInvoice(
        orderId,
        invoiceId,
        reason,
      );
      if (res && this.invoiceDetails?.id === invoiceId) {
        runInAction(() => {
          this.invoiceDetails = res;
          this.updateInvoiceRequest();
        });
      }
      return res;
    } catch (error: any) {
      throw error;
    }
  };

  clearInvoiceDetails = () => {
    this.invoiceRequest = {} as InvoiceRequest;
    this.invoiceDetails = {} as Invoice;
  };

  syncWithZoho = async (invoiceId: number) => {
    try {
      await this.invoiceAPI.syncInvoiceWithZoho(invoiceId);
    } catch (error: any) {
      throw error;
    }
  };

  recalculateInvoice = async (invoiceId: number) => {
    try {
      const response = await this.invoiceAPI.recalculateInvoice(invoiceId);
      if (response) {
        runInAction(() => {
          this.invoiceDetails = response;
          this.updateInvoiceRequest();
        });
      }
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  getAdvancePayments = async (orderId: number) => {
    try {
      const response = await this.invoiceAPI.getAdvancePayments(orderId);
      if (response) {
        this.advancePayments = response;
      }
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  updateAdvancePayment = async (value: InvoicePaymentRequest) => {
    try {
      const response = await this.invoiceAPI.updateAdvancePayment(value);
      if (response) {
        runInAction(() => {
          this.advancePayments = [];
          this.advancePayments = response;
        });
      }
    } catch (error: any) {
      throw error;
    }
  };

  editAdvancePayment = async (id: number, value: InvoicePaymentRequest) => {
    try {
      const response = await this.invoiceAPI.editAdvancePayment(id, value);
      if (response) {
        runInAction(() => {
          this.advancePayments = response;
        });
      }
    } catch (error: any) {
      throw error;
    }
  };

  deleteAdvancePayment = async (orderId: number, id: number) => {
    try {
      const response = await this.invoiceAPI.deleteAdvancePayment(orderId, id);
      if (response) {
        runInAction(() => {
          this.advancePayments = [];
          this.advancePayments = response;
        });
      }
    } catch (error: any) {
      throw error;
    }
  };

  downloadInvoicesInBackground = async (
    start_date: string,
    end_date: string,
  ) => {
    try {
      const response = await this.invoiceAPI.downloadInvoicesInBackground(
        start_date,
        end_date,
      );
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  updateInvoiceDate = async (value: string | null) => {
    runInAction(() => {
      this.invoiceRequest = {
        ...this.invoiceRequest,
        date: value,
      };
    });
  };

  getInvoices = async (filters: InvoiceFilters) => {
    try {
      const response = (await this.invoiceAPI.getInvoices(
        filters,
      )) as PaginatedAPIResponse<Invoice>;
      runInAction(() => {
        this.limit = response.limit;
        this.total_count = response.total_count;
        this.total_pages = response.total_pages;
        this.invoices = response.list;
      });
    } catch (error: any) {
      throw error;
    }
  };

  getPurchaseOrderInvoices = async (params: GetPurchaseOrderInvoicesParams) => {
    try {
      const response = await this.invoiceAPI.getPurchaseOrderInvoices(params);
      this.limit = response.limit;
      this.total_pages = response.total_pages;
      this.total_count = response.total_count;
      this.purchaseOrderInvoices = response.list;
    } catch (error: any) {
      throw error;
    }
  };

  approvePurchaseOrderInvoiceRequest = async (invoiceId: number) => {
    try {
      const response =
        await this.invoiceAPI.approvePurchaseOrderInvoiceRequest(invoiceId);
      runInAction(() => {
        this.purchaseOrderInvoices = this.purchaseOrderInvoices.map(
          (invoice) =>
            invoice.id === invoiceId
              ? {
                  ...invoice,
                  invoice_status: "APPROVED",
                  approved_by_admin_id: response?.result?.user?.id || null,
                  approved_invoice_date:
                    response?.result?.date || new Date().toISOString(),
                }
              : invoice,
        );
      });
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  rejectPurchaseOrderInvoiceRequest = async (
    invoiceId: number,
    rejectionReason?: string,
  ) => {
    try {
      const response = await this.invoiceAPI.rejectPurchaseOrderInvoiceRequest(
        invoiceId,
        rejectionReason,
      );
      runInAction(() => {
        this.purchaseOrderInvoices = this.purchaseOrderInvoices.map(
          (invoice) =>
            invoice.id === invoiceId
              ? {
                  ...invoice,
                  invoice_status: "REJECTED",
                  rejected_by_admin_id: response?.result?.user?.id || null,
                  invoice_status_message: response?.result?.message || null,
                  modified_date:
                    response?.result?.date || new Date().toISOString(),
                }
              : invoice,
        );
      });
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  markPurchaseOrderInvoicePaymentStatus = async (
    invoiceId: number,
    paymentStatus: string,
  ) => {
    try {
      const response =
        await this.invoiceAPI.markPurchaseOrderInvoicePaymentStatus(
          invoiceId,
          paymentStatus,
        );
      runInAction(() => {
        this.purchaseOrderInvoices = this.purchaseOrderInvoices.map(
          (invoice) =>
            invoice.id === invoiceId
              ? { ...invoice, invoice_status: paymentStatus }
              : invoice,
        );
      });
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  openApprovePurchaseOrderInvoicePopup = (invoiceId: number) => {
    runInAction(() => {
      this.approvePurchaseOrderInvoicePopup = {
        open: true,
        invoiceId: invoiceId,
      };
    });
  };

  closeApprovePurchaseOrderInvoicePopup = () => {
    runInAction(() => {
      this.approvePurchaseOrderInvoicePopup = { open: false, invoiceId: null };
    });
  };

  openRejectPurchaseOrderInvoicePopup = (invoiceId: number) => {
    runInAction(() => {
      this.rejectPurchaseOrderInvoicePopup = {
        open: true,
        invoiceId: invoiceId,
      };
    });
  };

  closeRejectPurchaseOrderInvoicePopup = () => {
    runInAction(() => {
      this.rejectPurchaseOrderInvoicePopup = { open: false, invoiceId: null };
    });
  };

  openInvoiceUploadDialog = (poId: number, vendorId: number) => {
    runInAction(() => {
      this.invoiceUploadState = {
        ...this.invoiceUploadState,
        dialogOpen: true,
        selectedPoId: poId,
        vendorId: vendorId,
      };
    });
  };

  closeInvoiceUploadDialog = () => {
    if (this.invoiceUploadState.isUploading) return;
    this.resetInvoiceUploadState();
  };

  resetInvoiceUploadState = () => {
    runInAction(() => {
      this.invoiceUploadState = {
        dialogOpen: false,
        selectedPoId: null,
        vendorId: null,
        invoiceFile: null,
        invoiceNumber: "",
        invoiceAmount: "",
        invoiceDate: null,
        isUploading: false,
        otherChargesEnabled: false,
        otherChargesAmount: "",
        productSelections: new Map(),
        invoiceFileUrl: "",
        productsAccordionExpanded: true,
      };
    });
  };

  setInvoiceUploadFile = (file: File | null) => {
    runInAction(() => {
      this.invoiceUploadState.invoiceFile = file;
    });
  };

  setInvoiceUploadNumber = (invoiceNumber: string) => {
    runInAction(() => {
      this.invoiceUploadState.invoiceNumber = invoiceNumber;
    });
  };

  setInvoiceUploadAmount = (invoiceAmount: string) => {
    runInAction(() => {
      this.invoiceUploadState.invoiceAmount = invoiceAmount;
    });
  };

  setInvoiceUploadDate = (invoiceDate: any) => {
    runInAction(() => {
      this.invoiceUploadState.invoiceDate = invoiceDate;
    });
  };

  validateInvoiceUpload = (): boolean => {
    if (!this.invoiceUploadState.invoiceFile) {
      return false;
    }

    if (!this.invoiceUploadState.invoiceNumber.trim()) {
      return false;
    }

    if (
      !this.invoiceUploadState.invoiceAmount ||
      Number(this.invoiceUploadState.invoiceAmount) <= 0
    ) {
      return false;
    }

    if (!this.invoiceUploadState.invoiceDate) {
      return false;
    }

    return true;
  };

  uploadPurchaseOrderInvoice = async (
    poId: number,
    invoiceFile: File,
    invoiceNumber: string,
    invoiceAmount: string,
    invoiceDate: string,
  ) => {
    runInAction(() => {
      this.invoiceUploadState.isUploading = true;
    });
    try {
      const imageAPI = new ImageAPI();
      const uploadedUrls = await imageAPI.uploadImage(
        [invoiceFile],
        "vendor-invoices",
      );

      if (!uploadedUrls || uploadedUrls.length === 0) {
        throw new Error("Failed to upload invoice file");
      }

      const uploadRequest: PurchaseOrderInvoiceUploadRequest = {
        po_id: poId,
        vendor_id: this.invoiceUploadState.vendorId || 0,
        invoice_url: uploadedUrls[0],
        invoice_number: invoiceNumber,
        invoice_amount: invoiceAmount,
        invoice_date: invoiceDate,
        other_charges:
          this.invoiceUploadState.otherChargesEnabled &&
          this.invoiceUploadState.otherChargesAmount
            ? parseFloat(this.invoiceUploadState.otherChargesAmount) || 0
            : undefined,
      };

      const response = await this.invoiceAPI.uploadInvoice(uploadRequest);

      return response;
    } catch (error: any) {
      throw error;
    } finally {
      runInAction(() => {
        this.invoiceUploadState.isUploading = false;
      });
    }
  };

  setOtherChargesEnabled = (enabled: boolean) => {
    runInAction(() => {
      this.invoiceUploadState.otherChargesEnabled = enabled;
    });
  };

  setOtherChargesAmount = (amount: string) => {
    runInAction(() => {
      this.invoiceUploadState.otherChargesAmount = amount;
    });
  };

  setProductSelections = (selections: Map<string, ProductSelectionState>) => {
    runInAction(() => {
      this.invoiceUploadState.productSelections = selections;
    });
  };

  setInvoiceFileUrl = (url: string) => {
    runInAction(() => {
      this.invoiceUploadState.invoiceFileUrl = url;
    });
  };

  setProductsAccordionExpanded = (expanded: boolean) => {
    runInAction(() => {
      this.invoiceUploadState.productsAccordionExpanded = expanded;
    });
  };

  getInvoiceActivityLogs = async (invoiceId: number) => {
    try {
      const response = await this.invoiceAPI.getInvoiceActivityLogs(invoiceId);
      runInAction(() => {
        this.activityLogs = response;
      });
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  clearActivityLogs = () => {
    this.activityLogs = [];
  };
}
